import { JSX, useContext, useEffect, useState } from "react";
import { PageSection } from "@app/shared";
import { QESAlert, QESCheckbox, QESSelect, QESTextArea } from "@app/shared/qes-standards";
import { CreateExceptionsContext } from "../contexts/CreateExceptionsContext";
import { ExceptionsFiltersContext } from "../contexts/ExceptionsFiltersContext";
import { WizardStepProps } from "../contexts/interfaces";
import { SpecialtyGroupExceptionStatus } from "@app/projects/results/common";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";

const CreateExceptionsDetails = (props: WizardStepProps): JSX.Element => {
  const wizardContext = useContext(CreateExceptionsContext);
  const filtersContext = useContext(ExceptionsFiltersContext);

  const [validStartDate, setValidStartDate] = useState<boolean>(true);
  const [validEndDate, setValidEndDate] = useState<boolean>(true);

  useEffect(() => {
    wizardContext.setStatus(SpecialtyGroupExceptionStatus.Pending);
  }, []);

  useEffect(() => {
    props.pageIsValid(!wizardContext.loading);
  }, [wizardContext.loading]);

  useEffect(() => {
    props.pageIsValid(validStartDate && validEndDate);
  }, [validStartDate, validEndDate]);

  useEffect(() => {
    if ((wizardContext.startDate && !wizardContext.endDate) ||
      (wizardContext.startDate && wizardContext.endDate && wizardContext.startDate < wizardContext.endDate) ||
      (!wizardContext.startDate)) {
      setValidStartDate(true);
    } else {
      setValidStartDate(false);
    }
  }, [wizardContext.startDate, wizardContext.endDate]);

  useEffect(() => {
    if ((wizardContext.endDate && !wizardContext.startDate) ||
      (wizardContext.startDate && wizardContext.endDate && wizardContext.endDate > wizardContext.startDate) ||
      (!wizardContext.endDate)) {
      setValidEndDate(true);
    } else {
      setValidEndDate(false);
    }
  }, [wizardContext.endDate, wizardContext.startDate]);

  const normalizeSpaces = (text: string): string => {
    if (!text) {
      return '';
    }
    return text.replace(/\s+/g, ' ').trim();
  };

  const handleCommentBlur = (e: React.FocusEvent<HTMLTextAreaElement>): void => {
    wizardContext.setComment(normalizeSpaces(e.target.value));
  }

  const handleNarrativeBlur = (e: React.FocusEvent<HTMLTextAreaElement>): void => {
    wizardContext.setNarrative(normalizeSpaces(e.target.value));
  }

  return (
    <PageSection>
      <div className="mt-3 col">
        <div className="row">
          <div className="col-6">
            <p className="font-weight-bolder">Add the details you would like to apply to the new exceptions.</p>
            <QESAlert type="info" persist={true} classNames="mt-3 mb-3">Editing information for selected exceptions will overwrite ALL previously entered information.</QESAlert>
            {wizardContext.error && (
              <QESAlert type="danger" persist={true} classNames="mt-3">
                {wizardContext.error}
              </QESAlert>
            )}

            <QESSelect
              id={"wizard-details__status-dropdown"}
              className="col-2"
              defaultValue={SpecialtyGroupExceptionStatus.Pending}
              label="Exception Status"
              onChange={(e) => wizardContext.setStatus(e.target.value as SpecialtyGroupExceptionStatus)}
              testId="wizard-details__status-dropdown"
              value={wizardContext.status}
            >
              {filtersContext.exceptionStatusOptions
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(option => (
                  <option key={option.key} value={option.key}>
                    {option.name}
                  </option>
                ))};
            </QESSelect>

            <div className="row">
              <QESDateInput
                containerClassName="mt-4 col-3"
                errorText="Start Date must be before Expiration Date"
                isValid={validStartDate}
                label="Start Date"
                testId="wizard-details__start-date"
                onChange={(e) => wizardContext.setStartDate(e.target.value)}
                unrequiredValidation={true}
                value={wizardContext.startDate}
              />

              <QESDateInput
                containerClassName="mt-4 col-3"
                errorText="Expiration Date must be after Start Date"
                isValid={validEndDate}
                label="Expiration Date"
                testId="wizard-details__end-date"
                onChange={(e) => wizardContext.setEndDate(e.target.value)}
                unrequiredValidation={true}
                value={wizardContext.endDate}
              />
            </div>

            <QESCheckbox
              containerClassName="mt-4"
              label="Previously Approved"
              checked={wizardContext.previouslyApproved}
              onChange={(e) => wizardContext.setPreviouslyApproved((e.target as HTMLInputElement).checked)}
              id="wizard-details__previously-approved"
              testId="wizard-details__previously-approved"
            />

            <QESTextArea
              id={"wizard-details__comment-input"}
              containerClassName="mt-4"
              rows={4}
              value={wizardContext.comment}
              onChange={(e) => wizardContext.setComment(e.target.value)}
              onBlur={(e) => handleCommentBlur(e)}
              maxLength={4000}
              label="Comments (Optional)"
              testId="wizard-details__comment-input"
            />

            <QESTextArea
              id={"wizard-details__narrative-input"}
              containerClassName="mt-3"
              rows={8}
              value={wizardContext.narrative}
              onChange={(e) => wizardContext.setNarrative(e.target.value)}
              onBlur={(e) => handleNarrativeBlur(e)}
              maxLength={4000}
              label="Exception Narrative (Optional)"
              testId="wizard-details__narrative-input"
            />

          </div>
        </div>
      </div>
    </PageSection>
  );
};

export default CreateExceptionsDetails;
